import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeftIcon,
  BuildingOffice2Icon,
  ChevronRightIcon,
  ComputerDesktopIcon,
  MagnifyingGlassIcon,
  MapPinIcon,
  PencilSquareIcon,
  PlusIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import toast, { Toaster } from 'react-hot-toast';
import ClinicBranchActionsMenu from '../../../components/MasterAdmin/ClinicBranchActionsMenu';
import DoctorClinicBranchModal from '../../../components/MasterAdmin/DoctorClinicBranchModal';
import DoctorClinicTimeslotEditorModal from '../../../components/MasterAdmin/DoctorClinicTimeslotEditorModal';
import { fetchDoctorClinicProfile } from '../../../services/MasterAdmin/doctorClinicService';
import {
  SpecialityOption,
  deleteBranchTimeslots,
  deleteClinicBranch,
  deleteTimeslot,
  deleteTimeslotsForBranchDay,
  fetchClinicBranches,
  fetchVendorSpecialities,
  fetchVendorTimeslots,
} from '../../../services/MasterAdmin/doctorClinicTimeslotService';
import { DoctorClinic, DoctorClinicBranch, DoctorClinicBranchTimeslot } from '../../../models/MasterAdmin/doctorClinic';
import { formatDoctorName } from '../../../utils/doctorName';
import {
  DAYS,
  WEEK_ORDER,
  extractTimeslotApiError,
  generateSlots,
  groupTemplatesBySpeciality,
  normConsultationType,
  normalizeWeekdayLabel,
  parseTimeToMinutes,
  sameBranchId,
  templateSpecialityKey,
} from '../../../utils/doctorClinicTimeslotUtils';

type ConsultationMode = 'online' | 'inclinic';

interface DeleteTarget {
  type: 'single' | 'day' | 'branch-timeslots' | 'branch';
  label: string;
  title?: string;
  confirmText?: string;
  timeSlotID?: string;
  day?: string;
  branch?: DoctorClinicBranch;
}

const DoctorClinicVendorTimeslotsPage: React.FC = () => {
  const { vendorId } = useParams<{ vendorId: string }>();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState<DoctorClinic | null>(null);
  const [branches, setBranches] = useState<DoctorClinicBranch[]>([]);
  const [allTimeslots, setAllTimeslots] = useState<DoctorClinicBranchTimeslot[]>([]);
  const [specialityOptions, setSpecialityOptions] = useState<SpecialityOption[]>([]);

  const [consultationMode, setConsultationMode] = useState<ConsultationMode>('inclinic');
  const [selectedBranchId, setSelectedBranchId] = useState<string | null>(null);

  const [searchQuery, setSearchQuery] = useState('');
  const [dayFilter, setDayFilter] = useState('all');
  const [refreshing, setRefreshing] = useState(false);

  const [branchModalOpen, setBranchModalOpen] = useState(false);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editorMode, setEditorMode] = useState<'create' | 'edit'>('create');
  const [editingSlot, setEditingSlot] = useState<DoctorClinicBranchTimeslot | null>(null);

  const [deleteTarget, setDeleteTarget] = useState<DeleteTarget | null>(null);
  const [deleting, setDeleting] = useState(false);

  const consultationTypes = profile?.consultationTypes || [];
  const supportsOnline = consultationTypes.some((t) => String(t).toLowerCase() === 'online');
  const supportsOffline = consultationTypes.some((t) => String(t).toLowerCase() === 'offline');

  const loadData = useCallback(async () => {
    if (!vendorId) return;
    setLoading(true);
    try {
      const [profileRes, branchRows, slotRows, specPayload] = await Promise.all([
        fetchDoctorClinicProfile(vendorId),
        fetchClinicBranches(vendorId, true),
        fetchVendorTimeslots(vendorId),
        fetchVendorSpecialities(vendorId),
      ]);
      setProfile(profileRes.data);
      setBranches(branchRows);
      setAllTimeslots(slotRows.filter((t) => t.isActive !== false));

      const options = Array.isArray(specPayload?.specialityOptions)
        ? specPayload.specialityOptions
        : (specPayload?.specialities || []).map((name) => ({
            specialityId: '',
            speciality: name,
          }));
      setSpecialityOptions(options.filter((o) => o.speciality));
    } catch (error) {
      console.error(error);
      toast.error('Failed to load timeslot data.');
    } finally {
      setLoading(false);
    }
  }, [vendorId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    if (loading) return;
    if (supportsOnline && !supportsOffline) {
      setConsultationMode('online');
    } else if (!supportsOnline && supportsOffline) {
      setConsultationMode('inclinic');
    }
  }, [loading, supportsOnline, supportsOffline]);

  const refreshBranches = async () => {
    if (!vendorId) return;
    const branchRows = await fetchClinicBranches(vendorId, true);
    setBranches(branchRows);
  };

  const refreshSlots = async () => {
    if (!vendorId) return;
    setRefreshing(true);
    try {
      const rows = await fetchVendorTimeslots(vendorId);
      setAllTimeslots(rows.filter((t) => t.isActive !== false));
    } catch {
      toast.error('Failed to refresh timeslots.');
    } finally {
      setRefreshing(false);
    }
  };

  const selectedBranch = branches.find((b) => b.clinicBranchId === selectedBranchId) || null;
  const isOnlineMode = consultationMode === 'online';
  const showingClinicList = isOnlineMode === false && !selectedBranchId;
  const showingTimeslots = isOnlineMode || Boolean(selectedBranchId);

  const locationTimeslots = useMemo(() => {
    if (!showingTimeslots) return [];
    if (isOnlineMode) {
      return allTimeslots.filter((t) => normConsultationType(t.consultationType) === 'Online');
    }
    if (!selectedBranchId) return [];
    return allTimeslots.filter(
      (t) =>
        sameBranchId(t.clinicBranchId, selectedBranchId) &&
        normConsultationType(t.consultationType) === 'Offline'
    );
  }, [allTimeslots, isOnlineMode, selectedBranchId, showingTimeslots]);

  const filteredTimeslots = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    return locationTimeslots
      .filter((t) => {
        if (dayFilter !== 'all' && normalizeWeekdayLabel(t.day) !== dayFilter) return false;
        if (!q) return true;
        return (
          String(t.speciality || '').toLowerCase().includes(q) ||
          String(t.day || '').toLowerCase().includes(q) ||
          String(t.startTime || '').toLowerCase().includes(q) ||
          String(t.endTime || '').toLowerCase().includes(q)
        );
      })
      .sort((a, b) => {
        const dayCmp =
          (WEEK_ORDER[normalizeWeekdayLabel(a.day).toLowerCase()] || 99) -
          (WEEK_ORDER[normalizeWeekdayLabel(b.day).toLowerCase()] || 99);
        if (dayCmp !== 0) return dayCmp;
        const specCmp = String(a.speciality || '').localeCompare(String(b.speciality || ''));
        if (specCmp !== 0) return specCmp;
        return parseTimeToMinutes(a.startTime) - parseTimeToMinutes(b.startTime);
      });
  }, [locationTimeslots, searchQuery, dayFilter]);

  const daysConfigured = useMemo(
    () => new Set(locationTimeslots.map((t) => normalizeWeekdayLabel(t.day))).size,
    [locationTimeslots]
  );

  const branchSlotCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    allTimeslots.forEach((t) => {
      if (t.clinicBranchId && normConsultationType(t.consultationType) === 'Offline') {
        counts[t.clinicBranchId] = (counts[t.clinicBranchId] || 0) + 1;
      }
    });
    return counts;
  }, [allTimeslots]);

  const onlineSlotCount = useMemo(
    () => allTimeslots.filter((t) => normConsultationType(t.consultationType) === 'Online').length,
    [allTimeslots]
  );

  const switchMode = (mode: ConsultationMode) => {
    setConsultationMode(mode);
    setSelectedBranchId(null);
    setSearchQuery('');
    setDayFilter('all');
  };

  const selectClinic = (branchId: string) => {
    setSelectedBranchId(branchId);
    setSearchQuery('');
    setDayFilter('all');
  };

  const backToClinicList = () => {
    setSelectedBranchId(null);
    setSearchQuery('');
    setDayFilter('all');
  };

  const openCreate = () => {
    setEditorMode('create');
    setEditingSlot(null);
    setEditorOpen(true);
  };

  const openEdit = (slot: DoctorClinicBranchTimeslot) => {
    setEditorMode('edit');
    setEditingSlot(slot);
    setEditorOpen(true);
  };

  const confirmDeleteSingle = (slot: DoctorClinicBranchTimeslot) => {
    setDeleteTarget({
      type: 'single',
      timeSlotID: slot.timeSlotID,
      label: `${normalizeWeekdayLabel(slot.day)} · ${slot.speciality || 'Timeslot'} · ${slot.startTime} – ${slot.endTime}`,
    });
  };

  const confirmDeleteDay = (day: string, sessions: DoctorClinicBranchTimeslot[]) => {
    setDeleteTarget({
      type: 'day',
      day,
      label: `all ${sessions.length} timeslot(s) on ${day}`,
      title: 'Delete timeslots for this day?',
    });
  };

  const confirmDeleteBranchTimeslots = (branch: DoctorClinicBranch) => {
    const count = branchSlotCounts[branch.clinicBranchId] || 0;
    setDeleteTarget({
      type: 'branch-timeslots',
      branch,
      label: `all ${count} timeslot(s) for "${branch.clinicName}"`,
      title: 'Delete all timeslots?',
      confirmText: 'Delete all timeslots',
    });
  };

  const confirmDeleteBranch = (branch: DoctorClinicBranch) => {
    setDeleteTarget({
      type: 'branch',
      branch,
      label: `"${branch.clinicName}"`,
      title: 'Delete clinic location?',
      confirmText: 'Delete clinic',
    });
  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget || !vendorId) return;

    if (deleteTarget.type === 'branch') {
      const count = deleteTarget.branch
        ? branchSlotCounts[deleteTarget.branch.clinicBranchId] || 0
        : 0;
      if (count > 0) {
        toast.error('Delete all timeslots for this clinic before deleting the location.');
        setDeleteTarget(null);
        return;
      }
    }

    setDeleting(true);
    try {
      if (deleteTarget.type === 'single' && deleteTarget.timeSlotID) {
        await deleteTimeslot(deleteTarget.timeSlotID);
        toast.success('Timeslot deleted permanently.');
      } else if (deleteTarget.type === 'day' && deleteTarget.day && selectedBranch) {
        await deleteTimeslotsForBranchDay(vendorId, selectedBranch.clinicBranchId, deleteTarget.day);
        toast.success(`Deleted all timeslots for ${deleteTarget.day}.`);
      } else if (deleteTarget.type === 'day' && deleteTarget.day) {
        const daySlots = locationTimeslots.filter(
          (t) => normalizeWeekdayLabel(t.day) === normalizeWeekdayLabel(deleteTarget.day!)
        );
        await Promise.all(daySlots.map((s) => deleteTimeslot(s.timeSlotID)));
        toast.success(`Deleted all timeslots for ${deleteTarget.day}.`);
      } else if (deleteTarget.type === 'branch-timeslots' && deleteTarget.branch) {
        const result = await deleteBranchTimeslots(vendorId, deleteTarget.branch.clinicBranchId);
        const count = result?.deletedCount ?? result?.deleted?.length ?? 0;
        if (count > 0) {
          toast.success('All timeslots for this clinic have been deleted.');
        } else {
          toast.success(result?.message || 'No timeslots found for this clinic.');
        }
        await refreshSlots();
        await refreshBranches();
      } else if (deleteTarget.type === 'branch' && deleteTarget.branch) {
        await deleteClinicBranch(deleteTarget.branch.clinicBranchId);
        toast.success('Clinic location deleted successfully.');
        if (sameBranchId(selectedBranchId, deleteTarget.branch.clinicBranchId)) {
          backToClinicList();
        }
        await refreshBranches();
        await refreshSlots();
      }
      setDeleteTarget(null);
      if (deleteTarget.type === 'single' || deleteTarget.type === 'day') {
        await refreshSlots();
      }
    } catch (error) {
      const err = error as { response?: { status?: number; data?: { message?: string; timeslotCount?: number } } };
      if (deleteTarget.type === 'branch' && err.response?.status === 409) {
        const timeslotCount = err.response?.data?.timeslotCount;
        const apiMessage = err.response?.data?.message;
        toast.error(
          timeslotCount != null
            ? `"${deleteTarget.branch?.clinicName}" has ${timeslotCount} timeslot(s). Delete all timeslots first.`
            : apiMessage || 'Cannot delete clinic while timeslots exist. Delete timeslots first.'
        );
      } else {
        toast.error(extractTimeslotApiError(error, 'Failed to complete delete.'));
      }
    } finally {
      setDeleting(false);
    }
  };

  const groupedSchedule = useMemo(() => {
    const map = new Map<string, DoctorClinicBranchTimeslot[]>();
    filteredTimeslots.forEach((t) => {
      const day = normalizeWeekdayLabel(t.day);
      if (!map.has(day)) map.set(day, []);
      map.get(day)!.push(t);
    });
    return Array.from(map.entries())
      .sort(
        ([a], [b]) => (WEEK_ORDER[a.toLowerCase()] || 99) - (WEEK_ORDER[b.toLowerCase()] || 99)
      )
      .map(([day, sessions]) => ({
        day,
        sessions,
        specialities: groupTemplatesBySpeciality(sessions),
      }));
  }, [filteredTimeslots]);

  const getSlotsForTimeslot = (slot: DoctorClinicBranchTimeslot) =>
    Array.isArray(slot.generatedSlots) && slot.generatedSlots.length > 0
      ? slot.generatedSlots
      : generateSlots(slot.startTime, slot.endTime, slot.intervalMinutes || 30);

  const locationLabel = isOnlineMode
    ? 'Online consultation'
    : selectedBranch?.clinicName || 'Clinic location';

  const handleBranchSaved = async () => {
    await refreshBranches();
    await refreshSlots();
  };

  if (loading) {
    return (
      <div className="flex min-h-[50vh] items-center justify-center">
        <div className="h-10 w-10 animate-spin rounded-full border-b-2 border-blue-600" />
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-6xl p-4 sm:p-6">
      <Toaster position="top-right" />

      <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
        <div className="flex items-start gap-3">
          <button
            type="button"
            onClick={() => {
              if (selectedBranchId) {
                backToClinicList();
              } else {
                navigate(`/vendors/doctor-clinic/${vendorId}`);
              }
            }}
            className="rounded-lg border border-gray-200 p-2 hover:bg-gray-50 dark:border-gray-600 dark:hover:bg-gray-700"
          >
            <ArrowLeftIcon className="h-5 w-5 text-gray-600 dark:text-gray-300" />
          </button>
          <div>
            <h1 className="text-xl font-bold text-gray-900 dark:text-white">Timeslot management</h1>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {profile ? formatDoctorName(profile.doctorName) : 'Doctor clinic'}
            </p>
            {!isOnlineMode && selectedBranch && (
              <div className="mt-1 flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400">
                <span>In-clinic</span>
                <ChevronRightIcon className="h-3 w-3" />
                <span className="font-medium text-gray-700 dark:text-gray-300">{selectedBranch.clinicName}</span>
              </div>
            )}
          </div>
        </div>

        {showingClinicList ? (
          <button
            type="button"
            onClick={() => setBranchModalOpen(true)}
            className="inline-flex items-center gap-2 rounded-lg bg-blue-700 px-4 py-2.5 text-sm font-semibold text-white hover:bg-blue-800"
          >
            <PlusIcon className="h-4 w-4" />
            Add clinic
          </button>
        ) : showingTimeslots ? (
          <div className="flex items-center gap-2">
            {!isOnlineMode && selectedBranch && (
              <ClinicBranchActionsMenu
                branch={selectedBranch}
                timeslotCount={branchSlotCounts[selectedBranch.clinicBranchId] || 0}
                onDeleteTimeslots={confirmDeleteBranchTimeslots}
                onDeleteBranch={confirmDeleteBranch}
              />
            )}
            <button
              type="button"
              onClick={openCreate}
              className="inline-flex items-center gap-2 rounded-lg bg-blue-700 px-4 py-2.5 text-sm font-semibold text-white hover:bg-blue-800"
            >
              <PlusIcon className="h-4 w-4" />
              Add timeslot
            </button>
          </div>
        ) : null}
      </div>

      {/* Consultation type tabs */}
      {(supportsOnline || supportsOffline) && (
        <div className="mb-6 flex flex-wrap gap-2 border-b border-gray-200 pb-4 dark:border-gray-700">
          {supportsOffline && (
            <button
              type="button"
              onClick={() => switchMode('inclinic')}
              className={`inline-flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-semibold transition-colors ${
                consultationMode === 'inclinic'
                  ? 'bg-blue-700 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-200'
              }`}
            >
              <BuildingOffice2Icon className="h-4 w-4" />
              In-clinic
            </button>
          )}
          {supportsOnline && (
            <button
              type="button"
              onClick={() => switchMode('online')}
              className={`inline-flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-semibold transition-colors ${
                consultationMode === 'online'
                  ? 'bg-sky-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-200'
              }`}
            >
              <ComputerDesktopIcon className="h-4 w-4" />
              Online
              <span className="rounded-full bg-white/20 px-1.5 py-0.5 text-xs">{onlineSlotCount}</span>
            </button>
          )}
        </div>
      )}

      {/* In-clinic: clinic picker */}
      {showingClinicList && (
        <div className="space-y-4">
          <div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Select a clinic</h2>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Add a clinic location first, then open it to configure weekly timeslots.
            </p>
          </div>

          {branches.length === 0 ? (
            <div className="rounded-xl border border-dashed border-gray-300 bg-gray-50 px-6 py-14 text-center dark:border-gray-600 dark:bg-gray-800/50">
              <BuildingOffice2Icon className="mx-auto mb-3 h-12 w-12 text-gray-300" />
              <p className="font-medium text-gray-700 dark:text-gray-200">No clinic locations yet</p>
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                Add the doctor&apos;s clinic address before creating in-clinic timeslots.
              </p>
              <button
                type="button"
                onClick={() => setBranchModalOpen(true)}
                className="mt-5 inline-flex items-center gap-1.5 rounded-lg bg-blue-700 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-800"
              >
                <PlusIcon className="h-4 w-4" />
                Add first clinic
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {branches.map((branch) => (
                <div
                  key={branch.clinicBranchId}
                  className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm dark:border-gray-700 dark:bg-gray-800"
                >
                  <div className="mb-3 flex items-start justify-between gap-2">
                    <div className="flex min-w-0 items-center gap-2">
                      <BuildingOffice2Icon className="h-6 w-6 shrink-0 text-blue-700 dark:text-blue-400" />
                      <h3 className="truncate font-semibold text-gray-900 dark:text-white">{branch.clinicName}</h3>
                    </div>
                    <div className="flex shrink-0 items-center gap-1">
                      <span className="rounded-full bg-blue-50 px-2 py-0.5 text-xs font-medium text-blue-700 dark:bg-blue-900/30 dark:text-blue-300">
                        {branchSlotCounts[branch.clinicBranchId] || 0} slots
                      </span>
                      <ClinicBranchActionsMenu
                        branch={branch}
                        timeslotCount={branchSlotCounts[branch.clinicBranchId] || 0}
                        onDeleteTimeslots={confirmDeleteBranchTimeslots}
                        onDeleteBranch={confirmDeleteBranch}
                      />
                    </div>
                  </div>
                  <p className="mb-2 flex items-start gap-1.5 text-sm text-gray-600 dark:text-gray-300">
                    <MapPinIcon className="mt-0.5 h-4 w-4 shrink-0" />
                    <span className="line-clamp-2">{branch.address || '—'}</span>
                  </p>
                  {branch.pincode && (
                    <p className="mb-4 text-xs text-gray-500">Pincode: {branch.pincode}</p>
                  )}
                  <button
                    type="button"
                    onClick={() => selectClinic(branch.clinicBranchId)}
                    className="text-sm font-medium text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
                  >
                    Manage timeslots →
                  </button>
                </div>
              ))}

              <button
                type="button"
                onClick={() => setBranchModalOpen(true)}
                className="flex min-h-[10rem] flex-col items-center justify-center rounded-xl border-2 border-dashed border-gray-300 bg-gray-50 p-5 text-gray-600 transition-colors hover:border-blue-400 hover:bg-blue-50/50 hover:text-blue-700 dark:border-gray-600 dark:bg-gray-800/50 dark:text-gray-300 dark:hover:border-blue-500"
              >
                <PlusIcon className="mb-2 h-8 w-8" />
                <span className="text-sm font-semibold">Add another clinic</span>
              </button>
            </div>
          )}
        </div>
      )}

      {/* Timeslots view (online or selected clinic) */}
      {showingTimeslots && (
        <>
          <div className="mb-4 grid gap-4 sm:grid-cols-3">
            <div className="rounded-xl border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800">
              <p className="text-xs font-medium uppercase tracking-wide text-gray-500">Location</p>
              <p className="mt-1 font-semibold text-gray-900 dark:text-white">{locationLabel}</p>
              {!isOnlineMode && selectedBranch?.address && (
                <p className="mt-1 line-clamp-2 text-xs text-gray-500">{selectedBranch.address}</p>
              )}
            </div>
            <div className="rounded-xl border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800">
              <p className="text-xs font-medium uppercase tracking-wide text-gray-500">Timeslots</p>
              <p className="mt-1 font-semibold text-gray-900 dark:text-white">{locationTimeslots.length}</p>
            </div>
            <div className="rounded-xl border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800">
              <p className="text-xs font-medium uppercase tracking-wide text-gray-500">Days configured</p>
              <p className="mt-1 font-semibold text-gray-900 dark:text-white">{daysConfigured} / 7</p>
            </div>
          </div>

          <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center">
            <div className="relative flex-1">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search by day, speciality, or time..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full rounded-lg border border-gray-300 py-2 pl-9 pr-3 text-sm dark:border-gray-600 dark:bg-gray-800 dark:text-white"
              />
            </div>
            <select
              value={dayFilter}
              onChange={(e) => setDayFilter(e.target.value)}
              className="rounded-lg border border-gray-300 px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-800 dark:text-white"
            >
              <option value="all">All days</option>
              {DAYS.map((d) => (
                <option key={d} value={d}>
                  {d}
                </option>
              ))}
            </select>
          </div>

          <div className="space-y-4">
            {refreshing ? (
              <div className="flex justify-center rounded-xl border border-gray-200 bg-white py-16 dark:border-gray-700 dark:bg-gray-800">
                <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-blue-600" />
              </div>
            ) : filteredTimeslots.length === 0 ? (
              <div className="rounded-xl border border-gray-200 bg-white px-6 py-14 text-center dark:border-gray-700 dark:bg-gray-800">
                <p className="text-gray-600 dark:text-gray-300">No timeslots for this location yet.</p>
                <button
                  type="button"
                  onClick={openCreate}
                  className="mt-4 inline-flex items-center gap-1.5 rounded-lg bg-blue-700 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-800"
                >
                  <PlusIcon className="h-4 w-4" />
                  Add first timeslot
                </button>
              </div>
            ) : (
              groupedSchedule.map(({ day, sessions, specialities }) => (
                <div
                  key={day}
                  className="overflow-hidden rounded-xl border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800"
                >
                  <div className="flex flex-wrap items-center justify-between gap-3 border-b border-gray-200 bg-gray-50 px-4 py-3 dark:border-gray-700 dark:bg-gray-700/40">
                    <div className="flex items-center gap-3">
                      <h3 className="text-base font-semibold text-gray-900 dark:text-white">{day}</h3>
                      <span className="rounded-full bg-blue-50 px-2 py-0.5 text-xs font-medium text-blue-700 dark:bg-blue-900/30 dark:text-blue-300">
                        {sessions.length} session{sessions.length !== 1 ? 's' : ''}
                      </span>
                    </div>
                    <button
                      type="button"
                      onClick={() => confirmDeleteDay(day, sessions)}
                      className="text-xs font-medium text-red-600 hover:underline dark:text-red-400"
                    >
                      Delete day
                    </button>
                  </div>

                  <div className="space-y-4 p-4">
                    {specialities.map((group) => (
                      <div
                        key={templateSpecialityKey(group)}
                        className="rounded-lg border border-blue-100 bg-blue-50/40 p-4 dark:border-blue-900/40 dark:bg-blue-900/10"
                      >
                        <span className="mb-3 inline-block rounded-full bg-blue-100 px-3 py-1 text-xs font-semibold text-blue-800 dark:bg-blue-900/40 dark:text-blue-200">
                          {group.speciality || 'General'}
                        </span>

                        <div className="space-y-3">
                          {group.templates.map((slot) => {
                            const slots = getSlotsForTimeslot(slot);
                            return (
                              <div
                                key={slot.timeSlotID}
                                className="rounded-lg border border-gray-200 bg-white p-3 dark:border-gray-600 dark:bg-gray-800"
                              >
                                <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                                  <div className="text-sm font-medium text-gray-800 dark:text-gray-200">
                                    {slot.startTime} – {slot.endTime}
                                    <span className="ml-2 text-xs font-normal text-gray-500 dark:text-gray-400">
                                      · {slot.intervalMinutes || 30} min · {slots.length} slots
                                    </span>
                                  </div>
                                  <div className="flex gap-2">
                                    <button
                                      type="button"
                                      onClick={() => openEdit(slot)}
                                      className="inline-flex items-center gap-1 rounded-md border border-gray-200 px-2.5 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700"
                                    >
                                      <PencilSquareIcon className="h-3.5 w-3.5" />
                                      Edit
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() => confirmDeleteSingle(slot)}
                                      className="inline-flex items-center gap-1 rounded-md border border-red-200 px-2.5 py-1.5 text-xs font-medium text-red-700 hover:bg-red-50 dark:border-red-900/50 dark:text-red-300 dark:hover:bg-red-900/20"
                                    >
                                      <TrashIcon className="h-3.5 w-3.5" />
                                      Delete
                                    </button>
                                  </div>
                                </div>
                                <div className="flex flex-wrap gap-1.5">
                                  {slots.map((time) => (
                                    <span
                                      key={`${slot.timeSlotID}-${time}`}
                                      className="rounded-md border border-gray-200 bg-gray-50 px-2 py-1 text-xs font-medium text-gray-700 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-300"
                                    >
                                      {time}
                                    </span>
                                  ))}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))
            )}
          </div>
        </>
      )}

      <DoctorClinicBranchModal
        open={branchModalOpen}
        vendorId={vendorId || ''}
        onClose={() => setBranchModalOpen(false)}
        onSaved={handleBranchSaved}
      />

      <DoctorClinicTimeslotEditorModal
        open={editorOpen}
        mode={editorMode}
        vendorId={vendorId || ''}
        consultationType={isOnlineMode ? 'Online' : 'Offline'}
        branchId={isOnlineMode ? null : selectedBranchId}
        branchName={locationLabel}
        specialityOptions={specialityOptions}
        existingTimeslots={locationTimeslots}
        editingSlot={editingSlot}
        onClose={() => setEditorOpen(false)}
        onSaved={refreshSlots}
      />

      {deleteTarget && (
        <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-md rounded-xl bg-white p-6 shadow-xl dark:bg-gray-800">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              {deleteTarget.title ||
                (deleteTarget.type === 'branch' ? 'Delete clinic location?' : 'Delete timeslot?')}
            </h3>
            <p className="mt-2 text-sm text-gray-600 dark:text-gray-300">
              {deleteTarget.type === 'branch-timeslots' ? (
                <>
                  This will permanently remove <strong>{deleteTarget.label}</strong>. The clinic location
                  will remain — you can add new timeslots later.
                </>
              ) : deleteTarget.type === 'branch' ? (
                <>
                  This will permanently delete clinic location <strong>{deleteTarget.label}</strong>.
                  {deleteTarget.branch &&
                  (branchSlotCounts[deleteTarget.branch.clinicBranchId] || 0) > 0 ? (
                    <span className="mt-2 block text-amber-700 dark:text-amber-300">
                      This clinic still has {branchSlotCounts[deleteTarget.branch.clinicBranchId]} timeslot(s).
                      Use &quot;Delete all timeslots&quot; first, then delete the clinic.
                    </span>
                  ) : (
                    <span className="mt-1 block">This cannot be undone.</span>
                  )}
                </>
              ) : (
                <>
                  This will permanently remove <strong>{deleteTarget.label}</strong>. This cannot be undone.
                  Existing bookings are not affected automatically — verify before deleting.
                </>
              )}
            </p>
            <div className="mt-6 flex gap-3">
              <button
                type="button"
                onClick={() => setDeleteTarget(null)}
                disabled={deleting}
                className="flex-1 rounded-lg border border-gray-300 px-4 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-200"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleConfirmDelete}
                disabled={
                  deleting ||
                  (deleteTarget.type === 'branch' &&
                    deleteTarget.branch != null &&
                    (branchSlotCounts[deleteTarget.branch.clinicBranchId] || 0) > 0)
                }
                className="flex-1 rounded-lg bg-red-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-red-700 disabled:opacity-50"
              >
                {deleting
                  ? 'Deleting...'
                  : deleteTarget.confirmText || 'Delete permanently'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DoctorClinicVendorTimeslotsPage;
